<template>
  <div class="main-container">
    <div class="diagnostics-page">
      <div class="admin-page-nav">
        <button class="admin-back-button" type="button" @click="goToAdminArea">
          <i class="fa-solid fa-arrow-left"></i>
          Area amministratore
        </button>
      </div>

      <header class="diagnostics-header">
        <div>
          <h1>Diagnostica estrazioni</h1>
        </div>
        <div class="diagnostics-header-actions">
          <button
            class="datev-settings-btn"
            type="button"
            title="Configura test parser DATEV"
            @click="openDatevDebugSettings"
          >
            <i class="fa-solid fa-gear"></i>
            DATEV
          </button>
          <button
            class="datev-settings-btn"
            type="button"
            title="Configura test parser F24"
            @click="openF24DebugSettings"
          >
            <i class="fa-solid fa-gear"></i>
            F24
          </button>
          <button
            class="datev-settings-btn"
            type="button"
            title="Configura test parser TeamSystem"
            @click="openTeamsystemDebugSettings"
          >
            <i class="fa-solid fa-gear"></i>
            TeamSystem
          </button>
          <button
            class="refresh-btn"
            type="button"
            :disabled="loading"
            @click="loadDiagnostics"
          >
            <i
              class="fa-solid fa-rotate-right"
              :class="{ 'fa-spin': loading }"
            ></i>
            {{ loading ? "Aggiorno..." : "Aggiorna" }}
          </button>
        </div>
      </header>

      <section class="diagnostics-toolbar">
        <label>
          Strumento
          <select
            v-model="filters.operation_type"
            @change="scheduleDiagnosticsFilter(0)"
          >
            <option value="">Tutti</option>
            <option
              v-for="option in conversionOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
        <label>
          Stato
          <select
            v-model="filters.status"
            @change="scheduleDiagnosticsFilter(0)"
          >
            <option value="">Tutti</option>
            <option value="failed">Failed</option>
            <option value="degraded">Degraded</option>
            <option value="warning">Warning</option>
          </select>
        </label>
        <label>
          Cerca
          <input
            v-model.trim="filters.search"
            placeholder="Studio, utente, file, job..."
            @input="scheduleDiagnosticsFilter"
            @keyup.enter="loadDiagnostics"
          />
        </label>
        <button
          v-if="hasActiveFilters"
          class="clear-filters-btn"
          type="button"
          @click="clearFilters"
        >
          <i class="fa-solid fa-xmark"></i>
          Pulisci
        </button>
        <div class="diagnostics-toolbar-actions">
          <button
            class="collapse-rows-btn"
            type="button"
            :disabled="loading || !diagnostics.length"
            :title="
              recordsCollapsed
                ? 'Mostra i record della tabella'
                : 'Nascondi i record della tabella'
            "
            @click="toggleDiagnosticRows"
          >
            <i
              class="fa-solid"
              :class="recordsCollapsed ? 'fa-eye' : 'fa-eye-slash'"
            ></i>
            {{ recordsCollapsed ? "Mostra righe" : "Nascondi righe" }}
          </button>
          <button
            class="delete-all-diagnostics-btn"
            type="button"
            :disabled="loading || deletingAll || !diagnosticsDeleteScopeCount"
            title="Elimina tutte le diagnostiche"
            @click="openDeleteAllConfirmModal"
          >
            <i class="fa-solid fa-trash-can"></i>
            {{ deletingAll ? "Elimino..." : "Elimina tutto" }}
          </button>
        </div>
      </section>

      <p v-if="error" class="diagnostics-error">{{ error }}</p>

      <section
        v-if="initialLoading"
        class="diagnostics-skeleton"
        aria-label="Caricamento diagnostiche"
      >
        <div class="diagnostics-skeleton-table">
          <div class="diagnostics-skeleton-head">
            <div
              v-for="column in 8"
              :key="`diagnostics-skeleton-head-${column}`"
              class="diagnostics-skeleton-line"
            ></div>
          </div>
          <div
            v-for="row in 6"
            :key="`diagnostics-skeleton-row-${row}`"
            class="diagnostics-skeleton-row"
          >
            <div
              v-for="column in 8"
              :key="`diagnostics-skeleton-cell-${row}-${column}`"
              class="diagnostics-skeleton-line"
              :class="{ short: column === 4 || column > 5 }"
            ></div>
          </div>
        </div>
      </section>

      <section
        v-else
        class="diagnostics-layout"
        :class="{ refreshing: loading }"
      >
        <div class="diagnostics-table-wrap">
          <table class="diagnostics-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Studio</th>
                <th>Utente</th>
                <th>File</th>
                <th>Strumento</th>
                <th>Job</th>
                <th>Stato</th>
                <th>Drop</th>
                <th>Warning</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in visibleDiagnostics"
                :key="item.id"
                :class="{ selected: selected && selected.id === item.id }"
                @click="selectDiagnostic(item)"
              >
                <td>{{ formatDate(item.created_at) }}</td>
                <td>{{ item.studio_name || item.studio_id }}</td>
                <td>{{ item.user_email }}</td>
                <td>{{ item.input_display_name }}</td>
                <td>
                  <span class="conversion-pill">
                    {{ formatOperationType(item.operation_type) }}
                  </span>
                </td>
                <td class="mono-cell">{{ shortId(item.job_id) }}</td>
                <td>
                  <span class="status-pill" :class="item.status">
                    {{ item.status }}
                  </span>
                </td>
                <td>{{ rawDropCount(item) }}</td>
                <td>{{ rawWarningCount(item) }}</td>
              </tr>
            </tbody>
          </table>
          <p
            v-if="recordsCollapsed && diagnostics.length"
            class="diagnostics-empty diagnostics-collapsed-message"
          >
            <i class="fa-solid fa-eye-slash"></i>
            Righe diagnostiche nascoste.
          </p>
          <p v-else-if="!diagnostics.length" class="diagnostics-empty">
            Nessuna diagnostica trovata.
          </p>
        </div>

        <section v-if="selected" class="diagnostics-detail">
          <div class="diagnostics-detail-topbar">
            <div class="diagnostics-detail-heading">
              <h2>{{ selected.input_display_name }}</h2>
              <span class="studio-context-pill">
                {{
                  selected.studio_name ||
                  selected.studio_id ||
                  "Studio non disponibile"
                }}
              </span>
            </div>
            <button
              class="diagnostics-detail-close"
              type="button"
              title="Chiudi dettaglio"
              @click="closeDiagnosticDetail"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <p>{{ selected.summary && selected.summary.message }}</p>

          <div class="diagnostics-detail-actions">
            <div class="diagnostic-file-actions">
              <div
                v-if="hasDiagnosticFile('input')"
                class="diagnostic-file-card"
              >
                <button type="button" @click="openPdfFile">
                  <i class="fa-solid fa-file-pdf"></i>
                  Apri PDF
                </button>
                <button
                  class="file-download-btn"
                  type="button"
                  title="Scarica PDF"
                  @click="downloadFile('input')"
                >
                  <i class="fa-solid fa-download"></i>
                </button>
              </div>
              <div v-if="hasDiagnosticFile('csv')" class="diagnostic-file-card">
                <button type="button" @click="openCsvFile">
                  <i class="fa-solid fa-table"></i>
                  Visualizza CSV
                </button>
                <button
                  class="file-download-btn"
                  type="button"
                  title="Scarica CSV"
                  @click="downloadFile('csv')"
                >
                  <i class="fa-solid fa-download"></i>
                </button>
              </div>
              <div
                v-if="hasDiagnosticFile('anomalies')"
                class="diagnostic-file-card"
              >
                <button type="button" @click="openJsonViewer('anomalies')">
                  <i class="fa-solid fa-code"></i>
                  Anomalie
                </button>
                <button
                  class="file-download-btn"
                  type="button"
                  title="Scarica anomalie"
                  @click="downloadFile('anomalies')"
                >
                  <i class="fa-solid fa-download"></i>
                </button>
              </div>
              <div
                v-if="hasDiagnosticFile('manifest')"
                class="diagnostic-file-card"
              >
                <button type="button" @click="openJsonViewer('manifest')">
                  <i class="fa-solid fa-code"></i>
                  Manifest
                </button>
                <button
                  class="file-download-btn"
                  type="button"
                  title="Scarica manifest"
                  @click="downloadFile('manifest')"
                >
                  <i class="fa-solid fa-download"></i>
                </button>
              </div>
              <button
                class="download-zip-btn"
                type="button"
                @click="downloadDiagnosticZip"
              >
                <i class="fa-solid fa-file-zipper"></i>
                Scarica pacchetto
              </button>
            </div>
            <button
              class="delete-diagnostic-btn"
              type="button"
              @click="openDeleteConfirmModal"
            >
              Elimina
            </button>
          </div>

          <section
            v-if="selectedSummaryEvents.length"
            class="interpreted-events-block"
          >
            <h3>Sintesi diagnostica</h3>
            <div class="interpreted-events-list">
              <article
                v-for="event in selectedSummaryEvents"
                :key="eventKey(event)"
                class="interpreted-event-card"
                :class="event.severity"
              >
                <span
                  v-if="hasProspettoPagheKey(event)"
                  class="interpreted-event-badge"
                >
                  PROSPETTO PAGHE
                </span>
                <div>
                  <strong>{{ formatSummaryEventTitle(event) }}</strong>
                  <span>{{ formatEventImpact(event) }}</span>
                </div>
                <p v-if="event.message">{{ event.message }}</p>
                <ul
                  v-if="event.details && event.details.length"
                  class="interpreted-event-details"
                >
                  <li
                    v-for="(detail, index) in event.details"
                    :key="`${eventKey(event)}-detail-${index}`"
                  >
                    {{ detail.message }}
                  </li>
                </ul>
                <small
                  v-if="
                    event.technical_event_types &&
                    event.technical_event_types.length
                  "
                >
                  Eventi tecnici: {{ event.technical_event_types.join(", ") }}
                </small>
                <ul
                  v-if="eventSections(event).length"
                  class="interpreted-event-sections"
                >
                  <li
                    v-for="section in eventSections(event)"
                    :key="`${eventKey(event)}-${section}`"
                  >
                    {{ section }}
                  </li>
                </ul>
                <small v-if="event.admin_message">
                  {{ event.admin_message }}
                </small>
              </article>
            </div>
          </section>

          <div v-if="detail" class="anomaly-block">
            <h3>Dettaglio tecnico</h3>
            <h4>Drop anomalies</h4>
            <p v-if="!dropAnomalies.length" class="anomaly-empty">
              Nessuna drop anomaly presente.
            </p>
            <ul v-else>
              <li
                v-for="(anomaly, index) in dropAnomalies"
                :key="`drop-${index}`"
              >
                <strong>Pag. {{ anomaly.page || "-" }}</strong>
                {{ anomaly.event_type }} - {{ anomaly.message }}
              </li>
            </ul>
            <h4>Section anomalies</h4>
            <p v-if="!sectionAnomalies.length" class="anomaly-empty">
              Nessuna section anomaly presente.
            </p>
            <ul v-else>
              <li
                v-for="(anomaly, index) in sectionAnomalies"
                :key="`section-${index}`"
              >
                <strong>Pag. {{ anomaly.page || "-" }}</strong>
                {{ anomaly.event_type }} - {{ anomaly.message }}
              </li>
            </ul>
          </div>
        </section>
      </section>
    </div>
    <PDFViewerFull
      v-if="pdfViewer.visible"
      :visible="pdfViewer.visible"
      :src="pdfViewer.url"
      :title="pdfViewer.title"
      @close="closePdfViewer"
      @download="downloadFile('input')"
    />
    <ExcelViewer
      v-if="csvViewer.visible"
      :rows="csvViewer.rows"
      :visible="csvViewer.visible"
      :title="csvViewer.title"
      @close="closeCsvViewer"
    />
    <div
      v-if="jsonViewer.visible"
      class="json-viewer-overlay"
      @click.self="closeJsonViewer"
    >
      <section class="json-viewer-panel">
        <header class="json-viewer-header">
          <div>
            <p>JSON diagnostica</p>
            <h3>{{ jsonViewer.title }}</h3>
          </div>
          <div class="json-viewer-actions">
            <button type="button" @click="copyJsonViewerContent">
              <i class="fa-solid fa-copy"></i>
              Copia
            </button>
            <button type="button" @click="downloadFile(jsonViewer.fileKey)">
              <i class="fa-solid fa-download"></i>
              Scarica
            </button>
            <button type="button" @click="closeJsonViewer">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </header>
        <pre
          class="json-viewer-content"
        ><code>{{ jsonViewer.content }}</code></pre>
      </section>
    </div>
    <ConfirmActionModal
      :visible="deleteConfirm.visible"
      :title="deleteConfirmTitle"
      :eyebrow="deleteConfirmEyebrow"
      :message="deleteConfirmMessage"
      :confirm-label="deleteConfirmLabel"
      cancel-label="Annulla"
      icon-class="fa-solid fa-triangle-exclamation"
      confirm-icon-class="fa-solid fa-trash"
      @close="closeDeleteConfirmModal"
      @confirm="confirmDeleteDiagnostic"
    />
    <div
      v-if="datevDebug.visible"
      class="datev-debug-overlay"
      @click.self="closeDatevDebugSettings"
    >
      <section class="datev-debug-panel">
        <header class="datev-debug-header">
          <div>
            <p>Configurazione personale</p>
            <h3>Test parser DATEV</h3>
          </div>
          <button type="button" @click="closeDatevDebugSettings">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div v-if="datevDebug.loading" class="datev-debug-loading">
          Caricamento impostazioni...
        </div>
        <div v-else class="datev-debug-body">
          <label class="datev-debug-toggle">
            <input
              v-model="datevDebug.settings.synthetic_anomalies_enabled"
              type="checkbox"
            />
            <span>
              <strong>Anomalie sintetiche</strong>
              <small>
                Aggiunge anomalie di test alle prossime estrazioni DATEV fatte
                da questo admin.
              </small>
            </span>
          </label>

          <section class="datev-debug-section">
            <h4>Sezioni riconosciute</h4>
            <p>
              Disattivare una voce la rimuove temporaneamente dai candidati del
              matcher DATEV. Non modifica l'inclusione iniziale delle sezioni
              nel legacy.
            </p>
            <div class="datev-debug-options">
              <label
                v-for="section in datevDebug.settings.recognized_sections"
                :key="`recognized-${section.id}`"
              >
                <input v-model="section.enabled" type="checkbox" />
                <span>{{ section.label }}</span>
              </label>
            </div>
          </section>

          <p v-if="datevDebug.error" class="datev-debug-error">
            {{ datevDebug.error }}
          </p>
          <p v-if="datevDebug.message" class="datev-debug-message">
            {{ datevDebug.message }}
          </p>
        </div>

        <footer class="datev-debug-actions">
          <button
            class="datev-debug-secondary"
            type="button"
            :disabled="datevDebug.saving"
            @click="resetDatevDebugSettings"
          >
            Ripristina default
          </button>
          <button
            class="datev-debug-primary"
            type="button"
            :disabled="datevDebug.loading || datevDebug.saving"
            @click="saveDatevDebugSettings"
          >
            {{ datevDebug.saving ? "Salvataggio..." : "Salva configurazione" }}
          </button>
        </footer>
      </section>
    </div>
    <div
      v-if="f24Debug.visible"
      class="datev-debug-overlay"
      @click.self="closeF24DebugSettings"
    >
      <section class="datev-debug-panel">
        <header class="datev-debug-header">
          <div>
            <p>Configurazione personale</p>
            <h3>Test parser F24</h3>
          </div>
          <button type="button" @click="closeF24DebugSettings">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div v-if="f24Debug.loading" class="datev-debug-loading">
          Caricamento impostazioni...
        </div>
        <div v-else class="datev-debug-body">
          <label class="datev-debug-toggle">
            <input
              v-model="f24Debug.settings.synthetic_anomalies_enabled"
              type="checkbox"
            />
            <span>
              <strong>Anomalie sintetiche</strong>
              <small>
                Aggiunge anomalie di test alle prossime estrazioni F24 fatte da
                questo admin con il nuovo estrattore.
              </small>
            </span>
          </label>
          <p v-if="f24Debug.error" class="datev-debug-error">
            {{ f24Debug.error }}
          </p>
          <p v-if="f24Debug.message" class="datev-debug-message">
            {{ f24Debug.message }}
          </p>
        </div>

        <footer class="datev-debug-actions">
          <button
            class="datev-debug-secondary"
            type="button"
            :disabled="f24Debug.loading || f24Debug.saving"
            @click="resetF24DebugSettings"
          >
            Ripristina default
          </button>
          <button
            class="datev-debug-primary"
            type="button"
            :disabled="f24Debug.loading || f24Debug.saving"
            @click="saveF24DebugSettings"
          >
            {{ f24Debug.saving ? "Salvataggio..." : "Salva configurazione" }}
          </button>
        </footer>
      </section>
    </div>
    <div
      v-if="teamsystemDebug.visible"
      class="datev-debug-overlay"
      @click.self="closeTeamsystemDebugSettings"
    >
      <section class="datev-debug-panel">
        <header class="datev-debug-header">
          <div>
            <p>Configurazione personale</p>
            <h3>Test parser TeamSystem</h3>
          </div>
          <button type="button" @click="closeTeamsystemDebugSettings">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>
        <div v-if="teamsystemDebug.loading" class="datev-debug-loading">
          Caricamento impostazioni...
        </div>
        <div v-else class="datev-debug-body">
          <label class="datev-debug-toggle">
            <input
              v-model="teamsystemDebug.settings.synthetic_anomalies_enabled"
              type="checkbox"
            />
            <span>
              <strong>Anomalie sintetiche</strong>
              <small>
                Aggiunge anomalie di test alle prossime estrazioni TeamSystem
                effettuate da questo admin.
              </small>
            </span>
          </label>
          <p v-if="teamsystemDebug.error" class="datev-debug-error">
            {{ teamsystemDebug.error }}
          </p>
          <p v-if="teamsystemDebug.message" class="datev-debug-message">
            {{ teamsystemDebug.message }}
          </p>
        </div>
        <footer class="datev-debug-actions">
          <button
            class="datev-debug-secondary"
            type="button"
            :disabled="teamsystemDebug.loading || teamsystemDebug.saving"
            @click="resetTeamsystemDebugSettings"
          >
            Ripristina default
          </button>
          <button
            class="datev-debug-primary"
            type="button"
            :disabled="teamsystemDebug.loading || teamsystemDebug.saving"
            @click="saveTeamsystemDebugSettings"
          >
            {{
              teamsystemDebug.saving ? "Salvataggio..." : "Salva configurazione"
            }}
          </button>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/config.js";
import PDFViewerFull from "@/components/PDFViewerFull.vue";
import ExcelViewer from "@/components/ExcelViewer.vue";
import ConfirmActionModal from "@/components/ConfirmActionModal.vue";

export default {
  name: "DiagnosticaConversioni",
  components: {
    PDFViewerFull,
    ExcelViewer,
    ConfirmActionModal,
  },
  data() {
    return {
      diagnostics: [],
      diagnosticsTotal: 0,
      selected: null,
      detail: null,
      loading: false,
      initialLoading: false,
      recordsCollapsed: false,
      deletingAll: false,
      error: "",
      filterDebounceTimer: null,
      requestSequence: 0,
      hasLoaded: false,
      filters: {
        operation_type: "",
        status: "",
        search: "",
      },
      conversionOptions: [
        { value: "prospetti_paghe", label: "Prospetti paghe" },
        { value: "modelli_f24", label: "Modelli F24" },
        { value: "bilancio_analitico", label: "Bilancio analitico" },
      ],
      pdfViewer: {
        visible: false,
        url: "",
        title: "",
      },
      csvViewer: {
        visible: false,
        rows: [],
        title: "",
      },
      jsonViewer: {
        visible: false,
        title: "",
        content: "",
        fileKey: "",
      },
      deleteConfirm: {
        visible: false,
        diagnostic: null,
        mode: "single",
      },
      datevDebug: {
        visible: false,
        loading: false,
        saving: false,
        error: "",
        message: "",
        settings: {
          synthetic_anomalies_enabled: false,
          recognized_sections: [],
        },
      },
      f24Debug: {
        visible: false,
        loading: false,
        saving: false,
        error: "",
        message: "",
        settings: { synthetic_anomalies_enabled: false },
      },
      teamsystemDebug: {
        visible: false,
        loading: false,
        saving: false,
        error: "",
        message: "",
        settings: { synthetic_anomalies_enabled: false },
      },
    };
  },
  computed: {
    dropAnomalies() {
      return this.detail?.anomalies?.drop_anomalies || [];
    },
    sectionAnomalies() {
      return this.detail?.anomalies?.section_anomalies || [];
    },
    hasActiveFilters() {
      return Boolean(
        this.filters.operation_type ||
          this.filters.status ||
          this.filters.search
      );
    },
    selectedAvailableFiles() {
      return this.selected?.available_files || {};
    },
    visibleDiagnostics() {
      return this.recordsCollapsed ? [] : this.diagnostics;
    },
    deleteConfirmTitle() {
      return this.deleteConfirm.mode === "all"
        ? "Eliminare tutte le diagnostiche?"
        : "Eliminare la diagnostica?";
    },
    deleteConfirmEyebrow() {
      return this.deleteConfirm.mode === "all"
        ? "Svuota diagnostica estrazioni"
        : "Diagnostica estrazione";
    },
    deleteConfirmLabel() {
      if (this.deleteConfirm.mode === "all" && this.deletingAll) {
        return "Elimino...";
      }
      return this.deleteConfirm.mode === "all" ? "Elimina tutto" : "Elimina";
    },
    diagnosticsDeleteScopeCount() {
      return Number(this.diagnosticsTotal || this.diagnostics.length || 0);
    },
    deleteConfirmMessage() {
      if (this.deleteConfirm.mode === "all") {
        const count = this.diagnosticsDeleteScopeCount;
        const scope = this.hasActiveFilters
          ? `le ${count} diagnostiche che corrispondono ai filtri correnti`
          : `tutte le ${count} diagnostiche salvate`;
        return (
          `Stai per eliminare definitivamente ${scope}.\n\n` +
          "Verranno rimossi i record nel database e le cartelle diagnostiche con i file collegati."
        );
      }

      const diagnostic = this.deleteConfirm.diagnostic || this.selected;
      const fileName = diagnostic?.input_display_name || "questa diagnostica";
      const studioName =
        diagnostic?.studio_name ||
        diagnostic?.studio_id ||
        "studio non disponibile";
      return (
        `Stai per eliminare definitivamente la diagnostica "${fileName}" ` +
        `dello studio "${studioName}".\n\n` +
        "Verranno rimossi sia il record nel database sia la cartella diagnostica con i file collegati."
      );
    },
    selectedSummary() {
      return this.detail?.diagnostic?.summary || this.selected?.summary || {};
    },
    selectedSummaryEvents() {
      return Array.isArray(this.selectedSummary.user_events)
        ? this.selectedSummary.user_events
        : [];
    },
  },
  mounted() {
    this.loadDiagnostics({ showSkeleton: true });
  },
  beforeDestroy() {
    clearTimeout(this.filterDebounceTimer);
    this.revokePdfViewerUrl();
  },
  methods: {
    goToAdminArea() {
      this.$router.push("/home/area-amministratore");
    },
    scheduleDiagnosticsFilter(delay = 350) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = setTimeout(() => {
        this.loadDiagnostics();
      }, delay);
    },
    clearFilters() {
      clearTimeout(this.filterDebounceTimer);
      this.filters.operation_type = "";
      this.filters.status = "";
      this.filters.search = "";
      this.loadDiagnostics();
    },
    toggleDiagnosticRows() {
      this.recordsCollapsed = !this.recordsCollapsed;
    },
    buildDiagnosticsFilterParams(includePagination = false) {
      const params = includePagination ? { per_page: 100 } : {};
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      return params;
    },
    async openDatevDebugSettings() {
      this.datevDebug.visible = true;
      this.datevDebug.message = "";
      await this.loadDatevDebugSettings();
    },
    closeDatevDebugSettings() {
      this.datevDebug.visible = false;
    },
    async loadDatevDebugSettings() {
      this.datevDebug.loading = true;
      this.datevDebug.error = "";
      try {
        const response = await axios.get(
          `${API_BASE_URL}/admin/conversion_diagnostics/datev_debug_settings`
        );
        this.datevDebug.settings = this.normalizeDatevDebugSettings(
          response.data.settings
        );
      } catch (err) {
        this.datevDebug.error =
          "Errore durante il caricamento della configurazione DATEV.";
        console.error(err);
      } finally {
        this.datevDebug.loading = false;
      }
    },
    normalizeDatevDebugSettings(settings = {}) {
      return {
        synthetic_anomalies_enabled: Boolean(
          settings.synthetic_anomalies_enabled
        ),
        recognized_sections: Array.isArray(settings.recognized_sections)
          ? settings.recognized_sections.map((section) => ({ ...section }))
          : [],
      };
    },
    buildDatevDebugPayload() {
      const disabledRecognizedSectionIds =
        this.datevDebug.settings.recognized_sections
          .filter((section) => !section.enabled)
          .map((section) => section.id);
      return {
        synthetic_anomalies_enabled: Boolean(
          this.datevDebug.settings.synthetic_anomalies_enabled
        ),
        disabled_recognized_section_ids: disabledRecognizedSectionIds,
      };
    },
    async saveDatevDebugSettings() {
      this.datevDebug.saving = true;
      this.datevDebug.error = "";
      this.datevDebug.message = "";
      try {
        const response = await axios.post(
          `${API_BASE_URL}/admin/conversion_diagnostics/datev_debug_settings`,
          this.buildDatevDebugPayload()
        );
        this.datevDebug.settings = this.normalizeDatevDebugSettings(
          response.data.settings
        );
        this.datevDebug.message =
          "Configurazione DATEV salvata per questo admin.";
      } catch (err) {
        this.datevDebug.error =
          "Errore durante il salvataggio della configurazione DATEV.";
        console.error(err);
      } finally {
        this.datevDebug.saving = false;
      }
    },
    async resetDatevDebugSettings() {
      this.datevDebug.settings.synthetic_anomalies_enabled = false;
      this.datevDebug.settings.recognized_sections =
        this.datevDebug.settings.recognized_sections.map((section) => ({
          ...section,
          enabled: true,
        }));
      await this.saveDatevDebugSettings();
    },
    async openF24DebugSettings() {
      this.f24Debug.visible = true;
      this.f24Debug.message = "";
      await this.loadF24DebugSettings();
    },
    closeF24DebugSettings() {
      this.f24Debug.visible = false;
    },
    async loadF24DebugSettings() {
      this.f24Debug.loading = true;
      this.f24Debug.error = "";
      try {
        const response = await axios.get(
          `${API_BASE_URL}/admin/conversion_diagnostics/f24_debug_settings`
        );
        this.f24Debug.settings.synthetic_anomalies_enabled = Boolean(
          response.data.settings?.synthetic_anomalies_enabled
        );
      } catch (err) {
        this.f24Debug.error =
          "Errore durante il caricamento della configurazione F24.";
        console.error(err);
      } finally {
        this.f24Debug.loading = false;
      }
    },
    async saveF24DebugSettings() {
      this.f24Debug.saving = true;
      this.f24Debug.error = "";
      this.f24Debug.message = "";
      try {
        const response = await axios.post(
          `${API_BASE_URL}/admin/conversion_diagnostics/f24_debug_settings`,
          {
            synthetic_anomalies_enabled: Boolean(
              this.f24Debug.settings.synthetic_anomalies_enabled
            ),
          }
        );
        this.f24Debug.settings.synthetic_anomalies_enabled = Boolean(
          response.data.settings?.synthetic_anomalies_enabled
        );
        this.f24Debug.message = "Configurazione F24 salvata per questo admin.";
      } catch (err) {
        this.f24Debug.error =
          "Errore durante il salvataggio della configurazione F24.";
        console.error(err);
      } finally {
        this.f24Debug.saving = false;
      }
    },
    async resetF24DebugSettings() {
      this.f24Debug.settings.synthetic_anomalies_enabled = false;
      await this.saveF24DebugSettings();
    },
    async openTeamsystemDebugSettings() {
      this.teamsystemDebug.visible = true;
      this.teamsystemDebug.message = "";
      await this.loadTeamsystemDebugSettings();
    },
    closeTeamsystemDebugSettings() {
      this.teamsystemDebug.visible = false;
    },
    async loadTeamsystemDebugSettings() {
      this.teamsystemDebug.loading = true;
      this.teamsystemDebug.error = "";
      try {
        const response = await axios.get(
          `${API_BASE_URL}/admin/conversion_diagnostics/teamsystem_debug_settings`
        );
        this.teamsystemDebug.settings.synthetic_anomalies_enabled = Boolean(
          response.data.settings?.synthetic_anomalies_enabled
        );
      } catch (err) {
        this.teamsystemDebug.error =
          "Errore durante il caricamento della configurazione TeamSystem.";
        console.error(err);
      } finally {
        this.teamsystemDebug.loading = false;
      }
    },
    async saveTeamsystemDebugSettings() {
      this.teamsystemDebug.saving = true;
      this.teamsystemDebug.error = "";
      this.teamsystemDebug.message = "";
      try {
        const response = await axios.post(
          `${API_BASE_URL}/admin/conversion_diagnostics/teamsystem_debug_settings`,
          {
            synthetic_anomalies_enabled: Boolean(
              this.teamsystemDebug.settings.synthetic_anomalies_enabled
            ),
          }
        );
        this.teamsystemDebug.settings.synthetic_anomalies_enabled = Boolean(
          response.data.settings?.synthetic_anomalies_enabled
        );
        this.teamsystemDebug.message =
          "Configurazione TeamSystem salvata per questo admin.";
      } catch (err) {
        this.teamsystemDebug.error =
          "Errore durante il salvataggio della configurazione TeamSystem.";
        console.error(err);
      } finally {
        this.teamsystemDebug.saving = false;
      }
    },
    async resetTeamsystemDebugSettings() {
      this.teamsystemDebug.settings.synthetic_anomalies_enabled = false;
      await this.saveTeamsystemDebugSettings();
    },
    async loadDiagnostics(options = {}) {
      clearTimeout(this.filterDebounceTimer);
      const requestId = ++this.requestSequence;
      const showSkeleton = options.showSkeleton || !this.hasLoaded;
      this.loading = true;
      this.initialLoading = showSkeleton;
      this.error = "";
      try {
        const response = await axios.get(
          `${API_BASE_URL}/admin/conversion_diagnostics`,
          { params: this.buildDiagnosticsFilterParams(true) }
        );
        if (requestId !== this.requestSequence) return;
        this.diagnostics = response.data.diagnostics || [];
        this.diagnosticsTotal = Number(
          response.data.total ?? this.diagnostics.length
        );
        this.hasLoaded = true;
        const selectedStillVisible =
          this.selected &&
          this.diagnostics.some((item) => item.id === this.selected.id);
        if (!selectedStillVisible) {
          this.selected = null;
          this.detail = null;
        }
      } catch (err) {
        if (requestId !== this.requestSequence) return;
        this.error = "Errore durante il caricamento della diagnostica.";
        console.error(err);
      } finally {
        if (requestId === this.requestSequence) {
          this.loading = false;
          this.initialLoading = false;
        }
      }
    },
    async selectDiagnostic(item) {
      this.selected = item;
      this.detail = null;
      try {
        const response = await axios.get(
          `${API_BASE_URL}/admin/conversion_diagnostics/${item.id}`
        );
        this.detail = response.data;
      } catch (err) {
        this.error = "Errore durante il caricamento del dettaglio.";
        console.error(err);
      }
    },
    closeDiagnosticDetail() {
      this.selected = null;
      this.detail = null;
    },
    async downloadFile(fileKey) {
      if (!this.selected) return;
      const response = await axios.get(
        `${API_BASE_URL}/admin/conversion_diagnostics/${this.selected.id}/download`,
        {
          params: { file: fileKey },
          responseType: "blob",
        }
      );
      this.downloadBlobResponse(response, this.fallbackDownloadName(fileKey));
    },
    async downloadDiagnosticZip() {
      if (!this.selected) return;
      const response = await axios.get(
        `${API_BASE_URL}/admin/conversion_diagnostics/${this.selected.id}/download_zip`,
        { responseType: "blob" }
      );
      const studioName =
        this.selected.studio_name || this.selected.studio_id || "Studio";
      const safeStudioName = this.safeFilenamePart(studioName);
      const fallbackName = `Anomalia-${safeStudioName}-${this.selected.id.slice(
        0,
        8
      )}.zip`;
      this.downloadBlobResponse(response, fallbackName);
    },
    async openPdfFile() {
      if (!this.selected || !this.hasDiagnosticFile("input")) return;
      const response = await this.fetchDiagnosticBlob("input");
      this.revokePdfViewerUrl();
      this.pdfViewer.url = window.URL.createObjectURL(response.data);
      this.pdfViewer.title = this.selected.input_display_name || "Input PDF";
      this.pdfViewer.visible = true;
    },
    closePdfViewer() {
      this.pdfViewer.visible = false;
      this.revokePdfViewerUrl();
      this.pdfViewer.title = "";
    },
    revokePdfViewerUrl() {
      if (this.pdfViewer.url) {
        window.URL.revokeObjectURL(this.pdfViewer.url);
        this.pdfViewer.url = "";
      }
    },
    async openCsvFile() {
      if (!this.selected || !this.hasDiagnosticFile("csv")) return;
      const response = await this.fetchDiagnosticBlob("csv");
      const text = await response.data.text();
      this.csvViewer.rows = this.parseCsvText(text);
      this.csvViewer.title = `${
        this.selected.input_display_name || "CSV"
      } - output`;
      this.csvViewer.visible = true;
    },
    closeCsvViewer() {
      this.csvViewer.visible = false;
      this.csvViewer.rows = [];
      this.csvViewer.title = "";
    },
    async openJsonViewer(fileKey) {
      if (!this.selected || !this.hasDiagnosticFile(fileKey)) return;
      await this.ensureSelectedDetailLoaded();
      const payload =
        fileKey === "manifest" ? this.detail?.manifest : this.detail?.anomalies;
      this.jsonViewer = {
        visible: true,
        title: fileKey === "manifest" ? "manifest.json" : "anomalies.json",
        content: JSON.stringify(payload || {}, null, 2),
        fileKey,
      };
    },
    closeJsonViewer() {
      this.jsonViewer.visible = false;
      this.jsonViewer.title = "";
      this.jsonViewer.content = "";
      this.jsonViewer.fileKey = "";
    },
    async copyJsonViewerContent() {
      if (!this.jsonViewer.content) return;
      await navigator.clipboard.writeText(this.jsonViewer.content);
    },
    async ensureSelectedDetailLoaded() {
      if (this.detail || !this.selected) return;
      const response = await axios.get(
        `${API_BASE_URL}/admin/conversion_diagnostics/${this.selected.id}`
      );
      this.detail = response.data;
    },
    fetchDiagnosticBlob(fileKey) {
      return axios.get(
        `${API_BASE_URL}/admin/conversion_diagnostics/${this.selected.id}/download`,
        {
          params: { file: fileKey },
          responseType: "blob",
        }
      );
    },
    downloadBlobResponse(response, fallbackName) {
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(
        /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i
      );
      const encodedName = filenameMatch ? filenameMatch[1] : "";
      const downloadName = encodedName
        ? decodeURIComponent(encodedName)
        : fallbackName;
      this.downloadBlob(response.data, downloadName);
    },
    downloadBlob(blob, fallbackName) {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fallbackName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    fallbackDownloadName(fileKey) {
      const baseName =
        this.selected?.input_display_name || this.selected?.id || "diagnostica";
      const names = {
        input: baseName,
        csv: "csv_output.csv",
        anomalies: "anomalies.json",
        manifest: "manifest.json",
        summary: "user_summary.json",
      };
      return (
        names[fileKey] || `${this.selected?.id || "diagnostica"}_${fileKey}`
      );
    },
    safeFilenamePart(value) {
      return String(value || "file")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 50);
    },
    rawDropCount(item) {
      return Number(item?.drop_anomalies_count ?? 0);
    },
    rawWarningCount(item) {
      return Number(item?.section_anomalies_count ?? 0);
    },
    eventKey(event) {
      const pages = Array.isArray(event.pages) ? event.pages.join("-") : "";
      return `${event.category || "event"}-${
        event.event_type || "unknown"
      }-${pages}`;
    },
    eventSections(event) {
      return Array.isArray(event.section_labels) ? event.section_labels : [];
    },
    hasProspettoPagheKey(event) {
      const sample = event?.sample || {};
      return Boolean(
        sample.denominazione_cliente ||
          sample.codice_ditta ||
          sample.month ||
          sample.year
      );
    },
    formatSummaryEventTitle(event) {
      const title = String(event?.title || "Anomalia rilevata")
        .replace(/^Prospetto Paghe escluso:\s*/i, "")
        .replace(/^Prospetto Paghe da verificare:\s*/i, "")
        .trim();
      if (/^Impossibile riconoscere pagin[ae]$/i.test(title)) {
        return "Layout Prospetto Paghe non riconosciuto";
      }
      return title;
    },
    formatEventImpact(event) {
      const count = Number(event.count || 0);
      const pageLabel = this.formatEventPages(event.pages);
      const countLabel = count > 1 ? `${count} occorrenze` : "1 occorrenza";
      return pageLabel ? `${countLabel} - ${pageLabel}` : countLabel;
    },
    formatEventPages(pages) {
      const validPages = Array.isArray(pages) ? pages : [];
      if (!validPages.length) return "";
      if (validPages.length === 1) return `pagina ${validPages[0]}`;
      if (validPages.length <= 4) {
        return `pagine ${validPages.join(", ")}`;
      }
      return `pagine ${validPages.slice(0, 4).join(", ")} e altre ${
        validPages.length - 4
      }`;
    },
    hasDiagnosticFile(fileKey) {
      return Boolean(this.selectedAvailableFiles[fileKey]);
    },
    parseCsvText(text) {
      const normalized = String(text || "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n");
      const lines = normalized.split("\n").filter((line) => line.length);
      if (!lines.length) return [];
      const delimiter = this.detectCsvDelimiter(lines[0]);
      return lines.map((line) => this.parseDelimitedLine(line, delimiter));
    },
    detectCsvDelimiter(line) {
      const delimiters = [";", ",", "\t"];
      return delimiters.reduce(
        (best, delimiter) => {
          const score = this.parseDelimitedLine(line, delimiter).length;
          return score > best.score ? { delimiter, score } : best;
        },
        { delimiter: ";", score: 0 }
      ).delimiter;
    },
    parseDelimitedLine(line, delimiter) {
      const cells = [];
      let cell = "";
      let quoted = false;
      for (let index = 0; index < line.length; index += 1) {
        const char = line[index];
        const next = line[index + 1];
        if (char === '"' && quoted && next === '"') {
          cell += '"';
          index += 1;
        } else if (char === '"') {
          quoted = !quoted;
        } else if (char === delimiter && !quoted) {
          cells.push(cell);
          cell = "";
        } else {
          cell += char;
        }
      }
      cells.push(cell);
      return cells;
    },
    openDeleteConfirmModal() {
      if (!this.selected) return;
      this.deleteConfirm = {
        visible: true,
        diagnostic: this.selected,
      };
    },
    closeDeleteConfirmModal() {
      this.deleteConfirm.visible = false;
      this.deleteConfirm.diagnostic = null;
      this.deleteConfirm.mode = "single";
    },
    openDeleteAllConfirmModal() {
      if (!this.diagnosticsDeleteScopeCount || this.deletingAll) return;
      this.deleteConfirm = {
        visible: true,
        diagnostic: null,
        mode: "all",
      };
    },
    async confirmDeleteDiagnostic() {
      if (this.deleteConfirm.mode === "all") {
        await this.confirmDeleteAllDiagnostics();
        return;
      }

      const diagnostic = this.deleteConfirm.diagnostic || this.selected;
      if (!diagnostic) return;

      try {
        await axios.delete(
          `${API_BASE_URL}/admin/conversion_diagnostics/${diagnostic.id}`
        );
        const deletedId = diagnostic.id;
        this.diagnostics = this.diagnostics.filter(
          (item) => item.id !== deletedId
        );
        this.diagnosticsTotal = Math.max(0, this.diagnosticsTotal - 1);
        if (this.selected?.id === deletedId) {
          this.selected = null;
          this.detail = null;
        }
        this.closeDeleteConfirmModal();
      } catch (err) {
        this.error = "Errore durante l'eliminazione della diagnostica.";
        console.error(err);
      }
    },
    async confirmDeleteAllDiagnostics() {
      if (this.deletingAll) return;

      this.deletingAll = true;
      try {
        await axios.delete(`${API_BASE_URL}/admin/conversion_diagnostics`, {
          params: this.buildDiagnosticsFilterParams(false),
        });
        this.diagnostics = [];
        this.diagnosticsTotal = 0;
        this.selected = null;
        this.detail = null;
        this.recordsCollapsed = false;
        this.closeDeleteConfirmModal();
        this.error = "";
      } catch (err) {
        this.error = "Errore durante l'eliminazione delle diagnostiche.";
        console.error(err);
      } finally {
        this.deletingAll = false;
      }
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("it-IT");
    },
    shortId(value) {
      if (!value) return "-";
      return String(value).slice(0, 8);
    },
    formatOperationType(value) {
      const option = this.conversionOptions.find(
        (item) => item.value === value
      );
      return option ? option.label : value || "-";
    },
  },
};
</script>

<style scoped>
.main-container {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: auto;
  width: 100% !important;
}

.diagnostics-page {
  flex: 1;
  width: 100%;
  max-width: 1220px;
  margin: 0 auto;
  padding: 32px 24px;
  box-sizing: border-box;
  color: #172033;
  text-align: left;
}

.admin-page-nav {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 18px;
}

.admin-back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  margin: 0;
  border: 1px solid #c7d0df;
  border-radius: 6px;
  padding: 0 14px;
  background: #ffffff;
  color: #26364d;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(38, 54, 77, 0.08);
}

.admin-back-button:hover {
  background: #eef3fa;
  border-color: #aebad0;
}

.diagnostics-header,
.diagnostics-toolbar,
.diagnostics-layout,
.diagnostic-file-actions {
  display: flex;
  gap: 16px;
}

.diagnostics-header {
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.diagnostics-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.diagnostics-header h1 {
  margin: 0;
  font-size: 28px;
}

.diagnostics-toolbar {
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: 18px;
  padding: 14px;
  border: 1px solid #d9e0ea;
  border-radius: 8px;
  background: #f7f9fc;
}

.diagnostics-toolbar label {
  display: grid;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #52627a;
}

.diagnostics-toolbar input,
.diagnostics-toolbar select {
  min-width: 180px;
  height: 38px;
  border: 1px solid #c7d0df;
  border-radius: 6px;
  padding: 0 10px;
  background: white;
}

.refresh-btn,
.datev-settings-btn,
.collapse-rows-btn,
.delete-all-diagnostics-btn,
.clear-filters-btn,
.json-viewer-actions button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 38px;
  border: 0;
  border-radius: 6px;
  padding: 0 14px;
  font-weight: 700;
  color: white;
  background: #2454a6;
  cursor: pointer;
}

.datev-settings-btn {
  padding: 0 10px;
  color: #2454a6;
  background: #ffffff;
  border: 1px solid #b8c8e3;
}

.datev-settings-btn:hover {
  background: #edf4ff;
}

.refresh-btn:disabled,
.clear-filters-btn:disabled {
  opacity: 0.68;
  cursor: wait;
}

.collapse-rows-btn:disabled,
.delete-all-diagnostics-btn:disabled {
  opacity: 0.58;
  cursor: not-allowed;
}

.clear-filters-btn {
  background: #52627a;
}

.diagnostics-toolbar-actions {
  display: inline-flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 10px;
  margin-left: auto;
}

.collapse-rows-btn {
  color: #2454a6;
  background: #ffffff;
  border: 1px solid #b8c8e3;
}

.collapse-rows-btn:not(:disabled):hover {
  background: #edf4ff;
}

.delete-all-diagnostics-btn {
  color: #ffffff;
  background: #a8323b;
  border: 1px solid #a8323b;
}

.delete-all-diagnostics-btn:not(:disabled):hover {
  background: #8f2931;
  border-color: #8f2931;
}

.delete-diagnostic-btn {
  height: 38px;
  border: 1px solid #c84f5a;
  border-radius: 6px;
  padding: 0 14px;
  font-weight: 700;
  color: #a8323b;
  background: #ffffff;
  cursor: pointer;
}

.delete-diagnostic-btn:hover {
  color: #ffffff;
  background: #a8323b;
}

.diagnostic-file-actions {
  flex-wrap: wrap;
  align-items: center;
  margin: 0;
}

.diagnostic-file-card {
  display: inline-flex;
  overflow: hidden;
  border-radius: 6px;
  border: 1px solid #b8c8e3;
  background: #ffffff;
}

.diagnostic-file-card button {
  height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 0;
  padding: 0 13px;
  color: #2454a6;
  background: #ffffff;
  font-weight: 800;
  cursor: pointer;
  box-shadow: none;
}

.diagnostic-file-card button:hover {
  background: #edf4ff;
}

.diagnostic-file-card .file-download-btn {
  width: 36px;
  justify-content: center;
  padding: 0;
  color: #2454a6;
  background: #f5f8fd;
  border-left: 1px solid #d6e0ef;
}

.diagnostic-file-card .file-download-btn:hover {
  color: #ffffff;
  background: #2454a6;
}

.download-zip-btn {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #6fb49e;
  border-radius: 6px;
  padding: 0 14px;
  color: #216e55;
  background: #ffffff;
  font-weight: 800;
  cursor: pointer;
}

.download-zip-btn:hover {
  color: #ffffff;
  background: #216e55;
  border-color: #216e55;
}

.diagnostics-layout {
  display: flex;
  flex-direction: column;
  gap: 18px;
  transition: opacity 0.18s ease;
}

.diagnostics-layout.refreshing {
  opacity: 0.72;
  pointer-events: none;
}

.diagnostics-skeleton {
  width: 100%;
}

.diagnostics-skeleton-table {
  width: 100%;
  min-height: 360px;
  overflow: hidden;
  border: 1px solid #d9e0ea;
  border-radius: 8px;
  background: #ffffff;
}

.diagnostics-skeleton-head,
.diagnostics-skeleton-row {
  display: grid;
  grid-template-columns: 1.15fr 0.9fr 1.05fr 0.65fr 1.6fr 0.8fr 0.45fr 0.6fr;
  gap: 16px;
  align-items: center;
  min-height: 58px;
  padding: 14px 18px;
}

.diagnostics-skeleton-head {
  background: #eef3fa;
}

.diagnostics-skeleton-row {
  border-top: 1px solid #e6ebf2;
}

.diagnostics-skeleton-line {
  width: 100%;
  height: 20px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef1f5, #dde3eb, #eef1f5);
  background-size: 220% 100%;
  animation: diagnostics-skeleton-pulse 1.4s infinite linear;
}

.diagnostics-skeleton-line.short {
  width: 82%;
}

.diagnostics-table-wrap {
  width: 100%;
  overflow: visible;
  border: 1px solid #d9e0ea;
  border-radius: 8px;
}

.diagnostics-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: auto;
}

.diagnostics-table th,
.diagnostics-table td {
  padding: 11px 12px;
  border-bottom: 1px solid #e6ebf2;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
}

.mono-cell {
  font-family: ui-monospace, SFMono-Regular, Consolas, "Liberation Mono",
    monospace;
  font-size: 12px;
  color: #52627a;
}

.diagnostics-table th {
  background: #eef3fa;
  font-size: 12px;
  color: #52627a;
  text-transform: uppercase;
}

.diagnostics-table tr {
  cursor: pointer;
}

.diagnostics-table tr.selected,
.diagnostics-table tr:hover {
  background: #edf4ff;
}

.status-pill {
  display: inline-flex;
  min-width: 74px;
  justify-content: center;
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 800;
  color: #172033;
  background: #dfe7f2;
}

.conversion-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  color: #254163;
  background: #eaf1fb;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.status-pill.failed {
  background: #ffd9dc;
  color: #931b28;
}

.status-pill.degraded {
  background: #ffe8bd;
  color: #7a4600;
}

.status-pill.warning {
  background: #fff5ba;
  color: #675900;
}

.diagnostics-detail {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d9e0ea;
  border-radius: 8px;
  padding: 18px;
  background: white;
}

.diagnostics-detail h2 {
  margin: 0;
  font-size: 19px;
  overflow-wrap: anywhere;
}

.diagnostics-detail-topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.diagnostics-detail-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}

.diagnostics-detail-close {
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  color: #647084;
  background: transparent;
  cursor: pointer;
}

.diagnostics-detail-close:hover {
  color: #172033;
  background: #eef3fa;
}

.studio-context-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 9px;
  color: #5e6878;
  background: #f2f4f7;
  border: 1px solid #e0e5ec;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.diagnostics-detail-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin: 14px 0 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e6ebf2;
}

.interpreted-events-block {
  margin: 0 0 18px;
  padding: 14px;
  border: 1px solid #e1e7f0;
  border-radius: 8px;
  background: #f8fafc;
}

.interpreted-events-block h3 {
  margin: 0 0 10px;
  color: #172033;
  font-size: 14px;
}

.interpreted-events-list {
  display: grid;
  gap: 10px;
}

.interpreted-event-card {
  display: grid;
  gap: 7px;
  padding: 12px;
  border: 1px solid #dde5ef;
  border-left: 4px solid #597196;
  border-radius: 7px;
  background: #ffffff;
}

.interpreted-event-card.blocking {
  border-left-color: #b22e3b;
}

.interpreted-event-card.warning {
  border-left-color: #c48a12;
}

.interpreted-event-card .interpreted-event-badge {
  width: max-content;
  padding: 3px 8px;
  color: #3d5b86;
  background: #edf3fb;
  border: 1px solid #d5e1f1;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 900;
  line-height: 1.1;
}

.interpreted-event-card div {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.interpreted-event-card strong {
  color: #162033;
  font-size: 14px;
}

.interpreted-event-card span {
  color: #637088;
  font-size: 12px;
  font-weight: 800;
}

.interpreted-event-card p {
  margin: 0;
  color: #303b4d;
  font-size: 13px;
  line-height: 1.4;
}

.interpreted-event-sections,
.interpreted-event-details {
  margin: 0;
  padding-left: 18px;
  color: #303b4d;
  font-size: 13px;
  line-height: 1.4;
}

.interpreted-event-details li + li {
  margin-top: 6px;
}

.interpreted-event-card small {
  color: #6a7484;
  font-size: 12px;
  line-height: 1.35;
}

.anomaly-block {
  overflow: visible;
}

.anomaly-block h3 {
  margin: 18px 0 8px;
  font-size: 14px;
}

.anomaly-block h4 {
  margin: 14px 0 8px;
  color: #25364d;
  font-size: 13px;
}

.anomaly-block ul {
  padding-left: 18px;
}

.anomaly-empty {
  margin: 0 0 12px;
  color: #687386;
  font-size: 13px;
  font-style: italic;
}

.anomaly-block li {
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.35;
}

.diagnostics-empty,
.diagnostics-error {
  padding: 16px;
  color: #52627a;
}

.diagnostics-collapsed-message {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.diagnostics-error {
  color: #9f1d2e;
}

.json-viewer-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  background: rgba(18, 24, 32, 0.58);
}

.json-viewer-panel {
  width: min(1080px, calc(100vw - 44px));
  max-height: calc(100vh - 44px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #cfd8e5;
  border-radius: 8px;
  background: #0f1724;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.32);
}

.json-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #263449;
  background: #172033;
  color: #ffffff;
}

.json-viewer-header p,
.json-viewer-header h3 {
  margin: 0;
}

.json-viewer-header p {
  color: #9fb0c7;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.json-viewer-header h3 {
  margin-top: 3px;
  font-size: 17px;
}

.json-viewer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.json-viewer-actions button {
  background: #2454a6;
}

.json-viewer-actions button:last-child {
  width: 38px;
  padding: 0;
  justify-content: center;
  background: #35435a;
}

.json-viewer-content {
  margin: 0;
  padding: 18px;
  overflow: auto;
  color: #d9e4f2;
  background: #0f1724;
  font-family: ui-monospace, SFMono-Regular, Consolas, "Liberation Mono",
    monospace;
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  tab-size: 2;
}

.json-viewer-content code {
  display: block;
  text-align: left;
}

.datev-debug-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
  background: rgba(18, 24, 32, 0.58);
}

.datev-debug-panel {
  width: min(760px, calc(100vw - 44px));
  max-height: calc(100vh - 44px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #d5dde8;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
}

.datev-debug-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid #e2e7ef;
  background: #f6f8fb;
}

.datev-debug-header p,
.datev-debug-header h3 {
  margin: 0;
}

.datev-debug-header p {
  color: #657186;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.datev-debug-header h3 {
  margin-top: 3px;
  color: #172033;
  font-size: 20px;
}

.datev-debug-header button {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 6px;
  color: #657186;
  background: transparent;
  cursor: pointer;
}

.datev-debug-header button:hover {
  color: #172033;
  background: #e8edf5;
}

.datev-debug-loading,
.datev-debug-body {
  min-height: 0;
  overflow-y: auto;
  padding: 18px;
}

.datev-debug-loading {
  color: #52627a;
}

.datev-debug-toggle {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 13px;
  border: 1px solid #d9e0ea;
  border-radius: 8px;
  background: #f8fafc;
}

.datev-debug-toggle input,
.datev-debug-options input {
  width: 18px;
  height: 18px;
  margin-top: 2px;
}

.datev-debug-toggle span {
  display: grid;
  gap: 4px;
}

.datev-debug-toggle strong {
  color: #172033;
}

.datev-debug-toggle small,
.datev-debug-section p {
  color: #657186;
  line-height: 1.4;
}

.datev-debug-section {
  margin-top: 18px;
}

.datev-debug-section h4 {
  margin: 0 0 4px;
  color: #172033;
  font-size: 15px;
}

.datev-debug-section p {
  margin: 0 0 10px;
  font-size: 13px;
}

.datev-debug-options {
  display: grid;
  gap: 8px;
}

.datev-debug-options label {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 11px;
  border: 1px solid #e0e7f1;
  border-radius: 6px;
  background: #ffffff;
}

.datev-debug-options span {
  color: #25364d;
  font-size: 14px;
  line-height: 1.35;
}

.datev-debug-error,
.datev-debug-message {
  margin: 14px 0 0;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.datev-debug-error {
  color: #9f1d2e;
  background: #fff0f2;
}

.datev-debug-message {
  color: #216e55;
  background: #effaf6;
}

.datev-debug-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid #e2e7ef;
  background: #fafafa;
}

.datev-debug-actions button {
  height: 38px;
  border-radius: 6px;
  padding: 0 14px;
  font-weight: 800;
  cursor: pointer;
}

.datev-debug-actions button:disabled {
  opacity: 0.65;
  cursor: wait;
}

.datev-debug-secondary {
  color: #52627a;
  background: #ffffff;
  border: 1px solid #c7d0df;
}

.datev-debug-primary {
  color: #ffffff;
  background: #2454a6;
  border: 1px solid #2454a6;
}

@keyframes diagnostics-skeleton-pulse {
  0% {
    background-position: 120% 0;
  }
  100% {
    background-position: -120% 0;
  }
}

@media (max-width: 980px) {
  .diagnostics-page {
    padding: 24px 16px;
  }

  .diagnostics-detail-actions {
    align-items: flex-start;
    flex-direction: column;
  }

  .diagnostics-skeleton-head,
  .diagnostics-skeleton-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .diagnostics-skeleton-line.short {
    width: 100%;
  }

  .diagnostics-toolbar-actions {
    width: 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
}
</style>
